import type { ICryptoAdapter } from '@app/domain/ports';
import type { KdfParams } from '@app/domain/entities';

type OpenPgpModule = typeof import('openpgp');

let openPgpPromise: Promise<OpenPgpModule> | undefined;

async function getOpenPgp(): Promise<OpenPgpModule> {
  if (!openPgpPromise) {
    openPgpPromise = import('openpgp').then((openpgp) => {
      // Configure OpenPGP.js once when the module is first requested.
      openpgp.config.showComment = false;
      openpgp.config.showVersion = false;
      openpgp.config.preferredCompressionAlgorithm = openpgp.enums.compression.zlib;
      // Use Argon2 S2K for password-based symmetric encryption (OpenPGP v6 / RFC 9580).
      // Existing messages encrypted with iterated S2K are still decryptable — the S2K
      // type is stored inside the message and selected automatically during decryption.
      openpgp.config.s2kType = openpgp.enums.s2k.argon2;
      return openpgp;
    });
  }

  return openPgpPromise;
}

/**
 * Cryptography adapter implementation using Web Crypto API and OpenPGP.js
 * Pure TypeScript implementation - framework-agnostic
 */
export class CryptoAdapter implements ICryptoAdapter {
  // Hash data with SHA-256
  async hash(data: string, salt: Uint8Array): Promise<Uint8Array> {
    const enc = new TextEncoder();
    const combined = new Uint8Array(enc.encode(data).length + salt.length);
    combined.set(enc.encode(data));
    combined.set(salt, enc.encode(data).length);
    const hashBuffer = await crypto.subtle.digest('SHA-256', combined);
    return new Uint8Array(hashBuffer);
  }

  // Generate random salt
  generateSalt(length = 16): Uint8Array {
    const s = new Uint8Array(length);
    crypto.getRandomValues(s);
    return s;
  }

  // Generate a random UUID (Version 4)
  generateUUID(): string {
    return crypto.randomUUID();
  }

  // Generate a random AES-GCM 256-bit Data Encryption Key (DEK)
  async generateDek(): Promise<CryptoKey> {
    return crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
  }

  // Derive an AES-KW key from a password or PIN using Argon2id (new keys) or PBKDF2 (legacy).
  async deriveKeyFromPassword(password: string, salt: Uint8Array, params: KdfParams): Promise<CryptoKey> {
    if (params.algorithm === 'Argon2id') {
      const { argon2id } = await import('hash-wasm');
      const keyBytes = await argon2id({
        password,
        salt,
        parallelism: params.parallelism,
        iterations: params.iterations,
        memorySize: params.memory,
        hashLength: 32,
        outputType: 'binary',
      });
      const keyBuffer = (keyBytes as Uint8Array).buffer.slice(0, 32) as ArrayBuffer;
      return crypto.subtle.importKey('raw', keyBuffer, 'AES-KW', false, ['wrapKey', 'unwrapKey']);
    }

    // PBKDF2 — legacy path for unwrapping existing keys only
    const enc = new TextEncoder();
    const pwKey = await crypto.subtle.importKey('raw', enc.encode(password), 'PBKDF2', false, ['deriveKey']);
    const saltBuffer = salt.buffer.slice(salt.byteOffset, salt.byteOffset + salt.byteLength) as ArrayBuffer;
    return crypto.subtle.deriveKey(
      { name: 'PBKDF2', salt: saltBuffer, iterations: params.iterations, hash: 'SHA-256' },
      pwKey,
      { name: 'AES-KW', length: 256 },
      true,
      ['wrapKey', 'unwrapKey'],
    );
  }

  // Derive a KEK from WebAuthn PRF output using HKDF
  async deriveKeyFromPrf(prfOutput: Uint8Array, salt: string, info: string): Promise<CryptoKey> {
    // Extract the actual buffer slice to ensure compatibility with Web Crypto API
    const buffer = prfOutput.buffer.slice(
      prfOutput.byteOffset,
      prfOutput.byteOffset + prfOutput.byteLength,
    ) as ArrayBuffer;
    const kekMaterial = await crypto.subtle.importKey('raw', buffer, 'HKDF', false, ['deriveKey']);
    const kek = await crypto.subtle.deriveKey(
      {
        name: 'HKDF',
        hash: 'SHA-256',
        salt: new TextEncoder().encode(salt),
        info: new TextEncoder().encode(info),
      },
      kekMaterial,
      { name: 'AES-KW', length: 256 },
      false,
      ['wrapKey', 'unwrapKey'],
    );
    return kek;
  }

  // Import raw key bytes as AES-GCM key
  async importAesKey(raw: Uint8Array): Promise<CryptoKey> {
    // Extract the actual buffer slice to ensure compatibility with Web Crypto API
    const buffer = raw.buffer.slice(raw.byteOffset, raw.byteOffset + raw.byteLength) as ArrayBuffer;
    return crypto.subtle.importKey('raw', buffer, { name: 'AES-GCM' }, true, ['encrypt', 'decrypt']);
  }

  // Export raw key bytes
  async exportRawKey(key: CryptoKey): Promise<Uint8Array> {
    const raw = await crypto.subtle.exportKey('raw', key);
    return new Uint8Array(raw);
  }

  // Wrap key with AES-KW
  async wrapKey(key: CryptoKey, wrappingKey: CryptoKey): Promise<Uint8Array> {
    const wrapped = await crypto.subtle.wrapKey('raw', key, wrappingKey, 'AES-KW');
    return new Uint8Array(wrapped);
  }

  // Unwrap key with AES-KW
  async unwrapKey(wrapped: Uint8Array, unwrappingKey: CryptoKey): Promise<CryptoKey> {
    // Extract the actual buffer slice to ensure compatibility with Web Crypto API
    const buffer = wrapped.buffer.slice(wrapped.byteOffset, wrapped.byteOffset + wrapped.byteLength) as ArrayBuffer;
    const unwrappedKey = await crypto.subtle.unwrapKey(
      'raw',
      buffer,
      unwrappingKey,
      'AES-KW',
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt'],
    );
    return unwrappedKey;
  }

  async encryptString(plaintext: string, key: CryptoKey): Promise<{ ciphertext: Uint8Array; iv: Uint8Array }> {
    const enc = new TextEncoder();
    const data = enc.encode(plaintext);
    const result = await this.encryptRaw(data, key);
    return result;
  }

  async decryptString(ciphertext: Uint8Array, iv: Uint8Array, key: CryptoKey): Promise<string> {
    const data = await this.decryptRaw(ciphertext, iv, key);
    const dec = new TextDecoder();
    return dec.decode(data);
  }

  // Encrypt string data with AES-GCM
  async encryptRaw(data: Uint8Array, key: CryptoKey): Promise<{ ciphertext: Uint8Array; iv: Uint8Array }> {
    const iv = this.generateSalt(12); // GCM recommended IV size is 12 bytes
    const ivBuffer = iv.buffer.slice(iv.byteOffset, iv.byteOffset + iv.byteLength) as ArrayBuffer;
    const dataBuffer = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer;
    const cipherBuffer = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: ivBuffer }, key, dataBuffer);
    return { ciphertext: new Uint8Array(cipherBuffer), iv };
  }

  // Decrypt string data with AES-GCM
  async decryptRaw(ciphertext: Uint8Array, iv: Uint8Array, key: CryptoKey): Promise<Uint8Array> {
    const ivBuffer = iv.buffer.slice(iv.byteOffset, iv.byteOffset + iv.byteLength) as ArrayBuffer;
    const ciphertextBuffer = ciphertext.buffer.slice(
      ciphertext.byteOffset,
      ciphertext.byteOffset + ciphertext.byteLength,
    ) as ArrayBuffer;
    const data = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: ivBuffer }, key, ciphertextBuffer);
    return new Uint8Array(data);
  }

  async encryptWithPgp(data: Uint8Array, passwords: string[], options?: Record<string, unknown>): Promise<Uint8Array> {
    const openpgp = await getOpenPgp();
    const message = await openpgp.createMessage({ binary: data });
    const encrypted = await openpgp.encrypt({
      ...options,
      message,
      passwords,
      format: 'binary',
    });

    return encrypted as Uint8Array;
  }

  async decryptWithPgp(data: Uint8Array, passwords: string[], options?: Record<string, unknown>): Promise<Uint8Array> {
    const openpgp = await getOpenPgp();
    const message = await openpgp.readMessage({ binaryMessage: data });
    const decrypted = await openpgp.decrypt({
      ...options,
      message,
      passwords,
      format: 'binary',
    });
    return decrypted.data as Uint8Array;
  }

  // PKCE (Proof Key for Code Exchange) methods for OAuth 2.0

  /**
   * Generate cryptographically secure random string using unreserved URL characters
   * RFC 3986: unreserved characters are [A-Z a-z 0-9 - . _ ~]
   */
  generateRandomString(length: number): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return Array.from(array, (byte) => charset[byte % charset.length]).join('');
  }

  /**
   * Generate PKCE code challenge from verifier (RFC 7636)
   * Uses SHA-256 hash and base64url encoding
   */
  async generatePkceChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);

    // Base64url encode: base64 then replace +/= with -_ and remove padding
    const bytes = new Uint8Array(digest);
    const binary = String.fromCharCode(...bytes);
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  /**
   * Generate PKCE code verifier (RFC 7636)
   * Returns 128-character random string (recommended length)
   */
  generatePkceVerifier(): string {
    return this.generateRandomString(128);
  }
}
